import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, limit, where, onSnapshot, getDocs, Timestamp } from 'firebase/firestore';
import PostCard from '../components/PostCard';
import HeroSection from '../components/HeroSection';
import { Loader2, AlertTriangle, TrendingUp, ShieldCheck, BookOpen, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ScamAlertBanner = ({ latestScam }) => {
  if (!latestScam) return null;

  return (
    <Link to={`/post/${latestScam.id}`} className="block glass-card rounded-xl p-5 border border-yellow-500/30 hover:border-yellow-500/60 transition-all">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500/20 to-red-500/20 flex items-center justify-center flex-shrink-0 mt-1">
          <AlertTriangle size={20} className="text-yellow-400" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-yellow-400 uppercase tracking-wide">Latest Scam Alert</span>
            {latestScam.verified && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <ShieldCheck size={12} /> Verified
              </span>
            )}
          </div>
          <p className="font-bold text-white text-sm line-clamp-2">{latestScam.title}</p>
          <p className="text-xs text-gray-400 mt-1 line-clamp-1">{latestScam.content}</p>
        </div>
      </div>
    </Link>
  );
};

const WeeklyStats = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-24">
        <Loader2 className="animate-spin text-cyan-400" size={24} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="glass-card p-4 rounded-lg text-center">
        <p className="text-2xl font-bold text-cyan-400">{stats.newReports}</p>
        <p className="text-xs text-gray-400 mt-1">New Reports (7d)</p>
      </div>
      <div className="glass-card p-4 rounded-lg text-center">
        <p className="text-2xl font-bold text-green-400">{stats.verified}</p>
        <p className="text-xs text-gray-400 mt-1">Verified (7d)</p>
      </div>
      <div className="glass-card p-4 rounded-lg text-center">
        <p className="text-2xl font-bold text-purple-400">{stats.botReports}</p>
        <p className="text-xs text-gray-400 mt-1">Intel Reports (7d)</p>
      </div>
      <div className="glass-card p-4 rounded-lg text-center">
        <p className="text-2xl font-bold text-yellow-400">{stats.topCategory || '—'}</p>
        <p className="text-xs text-gray-400 mt-1">Top Threat</p>
      </div>
    </div>
  );
};

const TopFraudCategories = ({ categories, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="animate-spin text-purple-400" size={24} />
      </div>
    );
  }

  if (categories.length === 0) {
    return <p className="text-center text-gray-500 py-8">No recent fraud data available.</p>;
  }

  const maxCount = categories[0]?.count || 1;

  return (
    <div className="space-y-3">
      {categories.map((cat) => (
        <div key={cat.name} className="glass-card rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-200">{cat.name}</span>
            <span className="text-xs font-bold text-cyan-400">{cat.count} reports</span>
          </div>
          <div className="w-full bg-white/5 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-cyan-500 to-purple-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.max((cat.count / maxCount) * 100, 8)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const LearnTeaser = () => {
  const guides = [
    { title: 'Researching Projects', desc: 'Due diligence before investing', path: '/learn/researching-projects', color: 'from-cyan-500/20 to-blue-500/20' },
    { title: 'Spotting Scams', desc: 'Red flags in crypto projects', path: '/learn/spotting-scams', color: 'from-yellow-500/20 to-orange-500/20' },
    { title: 'Pig Butchering', desc: 'Understanding romance-based fraud', path: '/learn/pig-butchering', color: 'from-red-500/20 to-pink-500/20' },
  ];

  return (
    <div className="glass-card rounded-xl p-5">
      <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
        <BookOpen size={20} className="text-purple-400" />
        Learn to Protect Yourself
      </h3>
      <div className="space-y-2">
        {guides.map((guide) => (
          <Link key={guide.path} to={guide.path} className="block glass-card p-3 rounded-lg hover:bg-white/5 transition-all group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${guide.color} flex items-center justify-center`}>
                  <BookOpen size={14} className="text-gray-200" />
                </div>
                <div>
                  <p className="font-medium text-gray-200 text-sm">{guide.title}</p>
                  <p className="text-xs text-gray-500">{guide.desc}</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
            </div>
          </Link>
        ))}
      </div>
      <Link to="/learn" className="block text-center text-sm text-cyan-400 hover:text-cyan-300 mt-3 transition-colors">
        View all guides →
      </Link>
    </div>
  );
};

export default function LandingPage() {
  const { initializing } = useAuth();
  const [recentPosts, setRecentPosts] = useState([]);
  const [latestScam, setLatestScam] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [weeklyStats, setWeeklyStats] = useState({ newReports: 0, verified: 0, botReports: 0, topCategory: '' });
  const [loadingStats, setLoadingStats] = useState(true);
  const [fraudCategories, setFraudCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch recent posts
  useEffect(() => {
    if (initializing) return;

    setLoadingPosts(true);
    const fetchPosts = async () => {
      try {
        const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'), limit(3));
        const snapshot = await getDocs(q);
        setRecentPosts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (e) {}
      setLoadingPosts(false);
    };
    fetchPosts();
  }, [initializing]);

  // Fetch latest scam alert (most recent CFE-type post)
  useEffect(() => {
    if (initializing) return;

    const fetchLatestScam = async () => {
      try {
        const q = query(
          collection(db, 'posts'),
          where('type', '==', 'CFE'),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const d = snapshot.docs[0];
          setLatestScam({ id: d.id, ...d.data() });
        }
      } catch (e) {}
    };
    fetchLatestScam();
  }, [initializing]);

  // Compute weekly stats and fraud categories
  useEffect(() => {
    if (initializing) return;

    const computeStats = async () => {
      try {
      setLoadingStats(true);
      setLoadingCategories(true);

      const sevenDaysAgo = Timestamp.fromMillis(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const recentQuery = query(
        collection(db, 'posts'),
        where('createdAt', '>=', sevenDaysAgo)
      );

      const snapshot = await getDocs(recentQuery);
      let verified = 0;
      let botReports = 0;
      const categoryCounts = {};

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.verified) verified++;
        if (data.botGenerated || data.authorId === 'cfe-daily-bot') botReports++;

        const matrixId = data.fraudMatrixId;
        if (matrixId && matrixId !== 'other' && matrixId !== 'F999') {
          categoryCounts[matrixId] = (categoryCounts[matrixId] || 0) + 1;
        }
      });

      // Resolve fraud matrix names
      let matrixMap = {};
      try {
        const matrixSnapshot = await getDocs(collection(db, 'fraudMatrix'));
        matrixSnapshot.forEach((doc) => {
          matrixMap[doc.id] = doc.data().name;
        });
      } catch (e) {
        // fraudMatrix collection may not exist yet
      }

      const sortedCategories = Object.entries(categoryCounts)
        .map(([id, count]) => ({ name: matrixMap[id] || id, count }))
        .sort((a, b) => b.count - a.count);

      const topCategory = sortedCategories.length > 0 ? sortedCategories[0].name : '';

      setWeeklyStats({
        newReports: snapshot.size,
        verified,
        botReports,
        topCategory: topCategory.length > 14 ? topCategory.substring(0, 12) + '…' : topCategory,
      });
      setFraudCategories(sortedCategories.slice(0, 5));
      setLoadingStats(false);
      setLoadingCategories(false);
      } catch (e) {
        setLoadingStats(false);
        setLoadingCategories(false);
      }
    };

    computeStats();
  }, [initializing]);

  return (
    <div className="space-y-10">
      <HeroSection />

      <ScamAlertBanner latestScam={latestScam} />

      <WeeklyStats stats={weeklyStats} loading={loadingStats} />

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <h2 className="text-3xl font-bold text-white mb-6">Recent Community Activity</h2>
          <div className="space-y-6">
            {loadingPosts ? (
              <div className="flex justify-center items-center h-48"><Loader2 className="animate-spin text-cyan-400" size={32} /></div>
            ) : recentPosts.length > 0 ? (
              recentPosts.map(post => <PostCard key={post.id} post={post} />)
            ) : (
              <p className="text-center text-gray-500 py-16">No recent posts to display.</p>
            )}
            <div className="text-center">
              <Link to="/home" className="inline-block bg-cyan-500/90 hover:bg-cyan-400 text-black font-bold py-3 px-8 rounded-lg text-sm transition-colors">
                  View Full Feed
              </Link>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-cyan-400" />
              Top Fraud Categories
            </h2>
            <TopFraudCategories categories={fraudCategories} loading={loadingCategories} />
          </div>

          <LearnTeaser />
        </div>
      </div>

      <footer className="text-center text-gray-500 text-sm mt-12 border-t border-white/10 pt-6">
        <p>&copy; {new Date().getFullYear()} CFE Portal. All Rights Reserved.</p>
        <div className="mt-2">
            <Link to="/disclaimer" className="hover:text-white transition-colors">
                Legal Disclaimer
            </Link>
        </div>
      </footer>
    </div>
  );
}
